import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  IconButton,
  useTheme,
  SelectChangeEvent,
  OutlinedInput,
  Chip,
  alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { logger } from 'utils/logger';
import subscriptionsService, { 
  Account, 
  Solution, 
  Subscription, 
  SummarySchedule,
  UserSolutionSubscriptionItem
} from '../services/SubscriptionsService';

// Create a component-specific logger
const log = logger.getLogger('AddSubscriptionDialog');

interface AddSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: number;
  userId: number;
  userName: string;
  onSubscriptionSaved: () => void;
  editMode?: boolean;
  subscription?: UserSolutionSubscriptionItem | null;
}

const AddSubscriptionDialog: React.FC<AddSubscriptionDialogProps> = ({ 
  open, 
  onClose, 
  accountId,
  userId, 
  userName,
  onSubscriptionSaved,
  editMode = false,
  subscription = null
}) => {
  const theme = useTheme();
  
  // Track initialization to prevent multiple API calls
  const initialized = useRef(false);
  
  // Form state
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [selectedSolutionId, setSelectedSolutionId] = useState<number | null>(null);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null);
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<number[]>([]);
  
  // Options for dropdowns
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [schedules, setSchedules] = useState<SummarySchedule[]>([]);
  const [showSchedules, setShowSchedules] = useState<boolean>(false);
  
  // Loading states
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);
  const [loadingSolutions, setLoadingSolutions] = useState<boolean>(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState<boolean>(false);
  const [loadingSchedules, setLoadingSchedules] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  
  // Validation
  const [errors, setErrors] = useState<{
    accountId?: string;
    solutionId?: string;
    subscriptionId?: string;
  }>({});
  
  // API Error state
  const [apiError, setApiError] = useState<string | null>(null);
  
  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    if (loadingAccounts) return; // Prevent duplicate calls
    
    try {
      // Clear any previous API errors
      setApiError(null);
      
      setLoadingAccounts(true);
      const result = await subscriptionsService.getAccounts();
      setAccounts(result.data);
    } catch (err) {
      log.error('Error fetching accounts:', err);
      
      // Set API error message to display to the user
      const errorMessage = err instanceof Error 
        ? `Failed to load accounts: ${err.message}` 
        : 'Failed to load accounts. Please try again.';
      setApiError(errorMessage);
    } finally {
      setLoadingAccounts(false);
    }
  }, [loadingAccounts]);
  
  // Fetch solutions for an account
  const fetchSolutions = useCallback(async (accountId: number, preserveSelection = false) => {
    if (loadingSolutions) return null; // Prevent duplicate calls
    
    try {
      // Clear any previous API errors
      setApiError(null);
      
      setLoadingSolutions(true);
      
      // Only reset solution ID if not preserving selection
      if (!preserveSelection) {
        setSelectedSolutionId(null);
      }
      
      const result = await subscriptionsService.getAccountSolutions(accountId);
      setSolutions(result.data);
      return result.data; // Return the solutions data
    } catch (err) {
      log.error(`Error fetching solutions for account ${accountId}:`, err);
      
      // Set API error message to display to the user
      const errorMessage = err instanceof Error 
        ? `Failed to load solutions: ${err.message}` 
        : 'Failed to load solutions. Please try again.';
      setApiError(errorMessage);
      return null; // Return null on error
    } finally {
      setLoadingSolutions(false);
    }
  }, [loadingSolutions]);
  
  // Fetch subscriptions for the dropdown
  const fetchSubscriptions = useCallback(async (accountId: number, preserveSelection = false) => {
    if (loadingSubscriptions) return; // Prevent duplicate calls
    
    try {
      // Clear any previous API errors
      setApiError(null);
      
      setLoadingSubscriptions(true);
      
      // Only reset subscription ID if not preserving selection
      if (!preserveSelection) {
        setSelectedSubscriptionId(null);
      }
      
      log.log(`Fetching subscriptions for account ${accountId}`);
      const result = await subscriptionsService.getAccountSubscriptions(accountId);
      
      if (result && result.data && Array.isArray(result.data)) {
        setSubscriptions(result.data);
      } else {
        log.error(`Invalid subscription data format received:`, result);
        setSubscriptions([]);
        setApiError('Invalid subscription data format received from server.');
      }
    } catch (err) {
      log.error(`Error fetching subscriptions for account ${accountId}:`, err);
      setSubscriptions([]);
      
      // Set API error message to display to the user
      const errorMessage = err instanceof Error 
        ? `Failed to load subscriptions: ${err.message}` 
        : 'Failed to load subscriptions. Please try again.';
      setApiError(errorMessage);
    } finally {
      setLoadingSubscriptions(false);
    }
  }, [loadingSubscriptions]);
  
  // Fetch summary schedules for a solution
  const fetchSchedules = useCallback(async (solutionId: number, preserveSelection = false) => {
    log.log(`fetchSchedules called with solutionId: ${solutionId}, preserveSelection: ${preserveSelection}, editMode: ${editMode}`);
    
    if (loadingSchedules) {
      log.log('Skipping fetchSchedules because loadingSchedules is true');
      return;
    }
    
    if (!solutionId) {
      log.log('Skipping fetchSchedules because solutionId is falsy');
      return;
    }
    
    try {
      // Clear any previous API errors
      setApiError(null);
      
      setLoadingSchedules(true);
      
      // Only reset schedule IDs if not preserving selection
      if (!preserveSelection) {
        setSelectedScheduleIds([]);
      }
      
      // Save current schedules to merge with new ones if in edit mode
      const currentSchedules = [...schedules];
      log.log('Current schedules before API call:', currentSchedules);
      
      log.log(`Making API call to getSolutionSummarySchedules with solutionId: ${solutionId}`);
      const result = await subscriptionsService.getSolutionSummarySchedules(solutionId);
      log.log('API response from getSolutionSummarySchedules:', result);
      
      // IMPORTANT: Only use the API response for the schedules dropdown
      // This ensures we only show valid schedules from the API
      
      // Process the API result to ensure all items have valid IDs
      const processedApiSchedules = result.map(schedule => ({
        ...schedule,
        id: schedule.id ?? 0, // Ensure id is never undefined
        name: schedule.name || `Schedule ${schedule.id ?? 0}`
      }));
      
      log.log('Processed API schedules:', processedApiSchedules);
      
      // Set the schedules state with only the API response
      setSchedules(processedApiSchedules as SummarySchedule[]);
      
      // If we're in edit mode, we need to make sure the selected schedule IDs
      // match the IDs from the API response
      if (editMode && preserveSelection && subscription && Array.isArray(subscription.summarySchedules)) {
        log.log('Edit mode: Checking if selected schedule IDs match API schedules');
        
        // Get the current selected schedule IDs
        const currentSelectedIds = [...selectedScheduleIds];
        log.log('Current selected schedule IDs:', currentSelectedIds);
        
        // Create a map of API schedules by summaryScheduleId for quick lookup
        const apiScheduleMap = new Map();
        processedApiSchedules.forEach(schedule => {
          apiScheduleMap.set(schedule.id, schedule);
        });
        
        // Log the available API schedule IDs
        log.log('Available API schedule IDs:', Array.from(apiScheduleMap.keys()));
        
        // Filter the selected IDs to only include those that exist in the API response
        const validSelectedIds = currentSelectedIds.filter(id => apiScheduleMap.has(id));
        
        // If the filtered list is different from the original, update the selected IDs
        if (validSelectedIds.length !== currentSelectedIds.length) {
          log.log('Updating selected schedule IDs to only include valid API schedules');
          log.log('Valid selected IDs:', validSelectedIds);
          setSelectedScheduleIds(validSelectedIds);
        }
      }
    } catch (err) {
      log.error(`Error fetching schedules for solution ${solutionId}:`, err);
      
      // Set API error message to display to the user
      const errorMessage = err instanceof Error 
        ? `Failed to load schedules: ${err.message}` 
        : 'Failed to load schedules. Please try again.';
      setApiError(errorMessage);
      
      // IMPORTANT: Don't use subscription schedules as fallback
      // Only show schedules from the API
      log.log('API call failed, setting schedules to empty array');
      setSchedules([]);
      
      // If we're in edit mode, we should also clear the selected schedule IDs
      // since we couldn't fetch valid schedules from the API
      if (editMode && selectedScheduleIds.length > 0) {
        log.log('Clearing selected schedule IDs due to API error');
        setSelectedScheduleIds([]);
      }
    } finally {
      setLoadingSchedules(false);
    }
  }, [loadingSchedules, editMode, subscription, schedules, selectedScheduleIds]);
  
  // Check feedback config for a solution and fetch schedules if needed
  const checkFeedbackConfig = useCallback((solutionId: number, preserveSelection = false) => {
    if (!solutionId) {
      // Don't reset showSchedules in edit mode with preserveSelection
      if (!(editMode && preserveSelection && subscription && Array.isArray(subscription.summarySchedules) && subscription.summarySchedules.length > 0)) {
        setShowSchedules(false);
      }
      return;
    }
    
    // Only reset schedule IDs if not preserving selection
    if (!preserveSelection) {
      setSelectedScheduleIds([]);
    }
    
    // In edit mode with schedules, don't reset showSchedules
    const isEditModeWithSchedules = editMode && 
                                   preserveSelection && 
                                   subscription && Array.isArray(subscription.summarySchedules) && subscription.summarySchedules.length > 0;
    
    if (!isEditModeWithSchedules) {
      setShowSchedules(false);
    }
    
    // Find the solution in our loaded solutions array
    const solution = solutions.find(s => s.id === solutionId);
    if (!solution) {
      log.error(`Solution ${solutionId} not found in loaded solutions.`);
      return;
    }
    
    // Check if the solution has feedback config and notifications
    const feedbackConfig = solution?.config?.feedback;
    
    // Log the feedback config for debugging
    log.log('Feedback config for solution:', feedbackConfig);
    
    if (feedbackConfig && feedbackConfig.notifications) {
      // Log the notification settings
      log.log('Notification settings:', feedbackConfig.notifications);
      
      // Check if either notification type is allowed
      // Make sure we're explicitly checking for boolean values
      const allowSummaryNotifications = feedbackConfig.notifications.allowSummaryNotifications === true;
      const allowFeedbackNotifications = feedbackConfig.notifications.allowFeedbackNotifications === true;
      
      const allowSummarySchedule = allowSummaryNotifications || allowFeedbackNotifications;
      
      log.log('Allow summary notifications:', allowSummaryNotifications);
      log.log('Allow feedback notifications:', allowFeedbackNotifications);
      log.log('Show schedules:', allowSummarySchedule);
      
      // In edit mode with existing schedules, keep showing schedules regardless of config
      if (isEditModeWithSchedules) {
        log.log('Edit mode with schedules - keeping schedules visible');
        // Always fetch schedules in edit mode
        fetchSchedules(solutionId, preserveSelection);
      } else {
        // Normal flow for new subscriptions
        setShowSchedules(allowSummarySchedule);
        
        if (allowSummarySchedule) {
          // Fetch schedules when notifications are allowed
          fetchSchedules(solutionId, preserveSelection);
        }
      }
    } else {
      // Log when notifications config is missing
      log.log('No notifications configuration found for solution:', solutionId);
      
      // In edit mode with existing schedules, keep showing schedules even if config is missing
      if (isEditModeWithSchedules) {
        log.log('Edit mode with schedules but no config - keeping schedules visible');
        // Always fetch schedules in edit mode, even if there's no feedback config
        fetchSchedules(solutionId, preserveSelection);
      }
    }
  }, [solutions, fetchSchedules, editMode, subscription]);
  
  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Reset state when dialog closes
      initialized.current = false;
    } else if (!initialized.current) {
      // Only initialize once when dialog opens
      initialized.current = true;
      
      // If editing mode, initialize form with subscription data
      if (editMode && subscription) {
        log.log('Initializing edit mode with subscription:', subscription);
        
        // Initially set to the current account ID
        // We'll update this later when we fetch the solution details
        log.log(`Initially setting account ID to current account ID: ${accountId}`);
        setSelectedAccountId(accountId);
        
        // If we have a solution ID, fetch the solution details to get the correct account ID
        if (subscription.solution?.id) {
          log.log(`Will fetch solution details for solution ID: ${subscription.solution.id} to get the correct account ID`);
        }
        
        // Handle solution and subscription selection
        if (subscription.solution?.id) {
          log.log(`Setting solution ID to ${subscription.solution.id} from subscription`);
          setSelectedSolutionId(subscription.solution.id);
        }
        
        if (subscription.subscription?.id) {
          log.log(`Setting subscription ID to ${subscription.subscription.id} from subscription`);
          setSelectedSubscriptionId(subscription.subscription.id);
        }
        
        // Handle schedule selection if any
        if (subscription.summarySchedules && subscription.summarySchedules.length > 0) {
          // Extract schedule IDs from the subscription and filter out undefined values
          const scheduleIds = subscription.summarySchedules
            .map(schedule => schedule?.summaryScheduleId)
            .filter((id): id is number => id !== undefined);
          setSelectedScheduleIds(scheduleIds);
          
          // IMPORTANT: Don't pre-populate schedules from subscription
          // We'll wait for the API call to get the valid schedules
          
          // Just store the selected schedule IDs for now
          log.log('Storing selected schedule IDs from subscription:', scheduleIds);
          
          // Show schedules section since we know we have schedules
          setShowSchedules(true);
          
          // Initialize schedules as empty array until API call completes
          setSchedules([]);
          log.log('Selected schedule IDs:', scheduleIds);
        }
        
        // Start loading data
        fetchAccounts();
        
        // Load in sequence to prevent duplicate/parallel calls
        const loadData = async () => {
          log.log('Starting loadData sequence for edit mode');
          
          // For edit mode, we need to get the correct account ID from the solution
          if (subscription.solution?.id) {
            try {
              log.log(`Fetching solution details for solution ID: ${subscription.solution.id}`);
              
              // Clear any previous API errors
              setApiError(null);
              
              // Fetch the solution details to get the account ID
              const solutionDetails = await subscriptionsService.getSolutionById(subscription.solution.id);
              
              if (solutionDetails && solutionDetails.account && solutionDetails.account.id) {
                const correctAccountId = solutionDetails.account.id;
                log.log(`Found correct account ID: ${correctAccountId} for solution ID: ${subscription.solution.id}`);
                
                // Update the selected account ID
                setSelectedAccountId(correctAccountId);
                
                // Fetch solutions and subscriptions for the correct account
                log.log(`Fetching solutions for correct account ID: ${correctAccountId}`);
                await fetchSolutions(correctAccountId, true);
                
                log.log(`Fetching subscriptions for correct account ID: ${correctAccountId}`);
                await fetchSubscriptions(correctAccountId, true);
              } else {
                log.log(`Could not find account ID in solution details, using current account ID: ${accountId}`);
                await fetchSolutions(accountId, true);
                await fetchSubscriptions(accountId, true);
              }
            } catch (err) {
              log.error(`Error fetching solution details for solution ID: ${subscription.solution.id}:`, err);
              
              // Set API error message to display to the user
              const errorMessage = err instanceof Error 
                ? `Failed to load solution details: ${err.message}` 
                : 'Failed to load solution details. Please try again.';
              setApiError(errorMessage);
              
              // Fall back to using the current account ID
              log.log(`Falling back to current account ID: ${accountId}`);
              await fetchSolutions(accountId, true);
              await fetchSubscriptions(accountId, true);
            }
          } else {
            // If there's no solution ID, just use the current account ID
            log.log(`No solution ID in subscription, using current account ID: ${accountId}`);
            await fetchSolutions(accountId, true);
            await fetchSubscriptions(accountId, true);
          }
          
          // Check feedback config after solutions are loaded
          if (subscription.solution?.id) {
            log.log(`Checking feedback config for solution ${subscription.solution.id} with preserveSelection=true`);
            checkFeedbackConfig(subscription.solution.id, true);
            
            // Also directly fetch schedules as a backup in case checkFeedbackConfig doesn't trigger it
            log.log(`Directly fetching schedules for solution ${subscription.solution.id} with preserveSelection=true`);
            await fetchSchedules(subscription.solution.id, true);
          } else {
            log.log('No solution ID found in subscription, skipping feedback config check');
          }
        };
        
        loadData();
      } else {
        // For new subscription, default to current account
        setSelectedAccountId(accountId);
        setSelectedSolutionId(null);
        setSelectedSubscriptionId(null);
        setSelectedScheduleIds([]);
        
        // Load in sequence to prevent duplicate/parallel calls
        fetchAccounts();
        const loadData = async () => {
          await fetchSolutions(accountId);
          await fetchSubscriptions(accountId);
        };
        
        loadData();
      }
    }
  }, [open, accountId, editMode, subscription, fetchAccounts, fetchSchedules, fetchSolutions, fetchSubscriptions, checkFeedbackConfig]);
  
  // Handle account change
  const handleAccountChange = (event: SelectChangeEvent<number | string>) => {
    const value = event.target.value;
    
    // Handle empty string or null
    if (value === '' || value === null) {
      setSelectedAccountId(null);
      setSelectedSolutionId(null);
      setSelectedSubscriptionId(null);
      setSelectedScheduleIds([]);
      setShowSchedules(false);
      return;
    }
    
    // Convert to number
    const accountId = Number(value);
    setSelectedAccountId(accountId);
    setSelectedSolutionId(null);
    setSelectedSubscriptionId(null);
    setSelectedScheduleIds([]);
    setShowSchedules(false);
    
    // Reset errors
    setErrors(prev => ({
      ...prev,
      accountId: undefined
    }));
    
    // Fetch related data for the new account
    fetchSolutions(accountId);
    fetchSubscriptions(accountId);
  };
  
  // Handle solution change
  const handleSolutionChange = (event: SelectChangeEvent<number | string>) => {
    const value = event.target.value;
    
    // Handle empty string or null
    if (value === '' || value === null) {
      setSelectedSolutionId(null);
      setShowSchedules(false);
      return;
    }
    
    // Convert to number
    const solutionId = Number(value);
    setSelectedSolutionId(solutionId);
    
    // Reset errors
    setErrors(prev => ({
      ...prev,
      solutionId: undefined
    }));
    
    // Check feedback config to determine if schedules should be shown
    if (solutionId) {
      checkFeedbackConfig(solutionId);
    } else {
      setShowSchedules(false);
    }
  };
  
  // Handle subscription change
  const handleSubscriptionChange = (event: SelectChangeEvent<number | string>) => {
    const value = event.target.value;
    
    // Handle empty string or null
    if (value === '' || value === null) {
      setSelectedSubscriptionId(null);
      return;
    }
    
    // Convert to number
    const subscriptionId = Number(value);
    setSelectedSubscriptionId(subscriptionId);
    
    // Reset errors
    setErrors(prev => ({
      ...prev,
      subscriptionId: undefined
    }));
  };
  
  // Handle schedules change
  const handleSchedulesChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    
    // Log the selected values for debugging
    log.log('Selected schedule IDs:', value);
    
    // For each selected ID, log whether it matches a summaryScheduleId or id in our schedules
    value.forEach(selectedId => {
      const matchingSchedule = schedules.find(s => 
        s.summaryScheduleId === selectedId || s.id === selectedId
      );
      
      if (matchingSchedule) {
        log.log(`Selected ID ${selectedId} matches schedule:`, matchingSchedule);
        if (matchingSchedule.summaryScheduleId !== undefined && matchingSchedule.id !== undefined) {
          if (matchingSchedule.summaryScheduleId === matchingSchedule.id) {
            log.log(`Selection: Match found! summaryScheduleId and id both equal ${matchingSchedule.id}`);
          } else {
            log.log(`Selection: Using ${selectedId}, which matches ${selectedId === matchingSchedule.summaryScheduleId ? 'summaryScheduleId' : 'id'}`);
          }
        }
      } else {
        log.log(`Selected ID ${selectedId} does not match any loaded schedule`);
      }
    });
    
    setSelectedScheduleIds(value);
  };
  
  // Validate form before submission
  const validateForm = (): boolean => {
    const newErrors: {
      accountId?: string;
      solutionId?: string;
      subscriptionId?: string;
    } = {};
    
    if (!selectedAccountId) {
      newErrors.accountId = 'Account is required';
    }
    
    if (!selectedSolutionId) {
      newErrors.solutionId = 'Solution is required';
    }
    
    if (!selectedSubscriptionId) {
      newErrors.subscriptionId = 'Subscription is required';
    }
    
    setErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e?: React.FormEvent) => {
    // Prevent default form submission behavior if event is provided
    if (e) {
      e.preventDefault();
    }
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Clear any previous API errors
      setApiError(null);
      
      setSaving(true);
      
      // Log the current state before submission
      log.log('Submitting with showSchedules:', showSchedules);
      log.log('Selected schedule IDs:', selectedScheduleIds);
      
      // Ensure accountId is a number (not null) before submitting
      if (!selectedAccountId) {
        setErrors(prev => ({ ...prev, accountId: 'Account is required' }));
        return;
      }
      
      const data = {
        accountId: selectedAccountId, // Now we know this is a number, not null
        solutionId: selectedSolutionId as number,
        subscriptionId: selectedSubscriptionId as number,
        summaryScheduleIds: showSchedules && selectedScheduleIds.length > 0 ? selectedScheduleIds : undefined
      };
      
      // Log the data being sent
      log.log('Submitting data:', data);
      
      if (editMode && subscription) {
        await subscriptionsService.updateUserSubscription(userId, subscription.id, data);
      } else {
        await subscriptionsService.addSubscriptionToUser(userId, data);
      }
      
      // Notify parent component of successful save
      onSubscriptionSaved();
      
      // Close dialog after successful save
      onClose();
      
    } catch (err) {
      log.error('Error saving subscription:', err);
      
      // Set API error message to display to the user
      const errorMessage = err instanceof Error 
        ? `Failed to save subscription: ${err.message}` 
        : 'Failed to save subscription. Please try again.';
      setApiError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon 
            color="primary"
            sx={{ mr: 1.5, fontSize: '1.8rem' }}
          />
          <Typography variant="h6" component="span">
            {editMode ? 'Edit Subscription' : 'Add Subscription'} for {userName}
          </Typography>
        </Box>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={onClose} 
          disabled={saving}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {/* API Error Alert */}
        {apiError && (
          <Box 
            sx={{ 
              mb: 2, 
              p: 2, 
              borderRadius: 1, 
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
            }}
          >
            <Typography variant="body2" fontWeight="medium">
              {apiError}
            </Typography>
          </Box>
        )}
        
        {/* Account Selection */}
        <FormControl 
          fullWidth 
          margin="normal" 
          error={!!errors.accountId}
          disabled={loadingAccounts || saving || editMode} // Disable editing account in edit mode
        >
          <InputLabel id="account-select-label">Account</InputLabel>
          <Select
            labelId="account-select-label"
            id="account-select"
            value={selectedAccountId ?? ''}
            onChange={handleAccountChange}
            label="Account"
            displayEmpty
            renderValue={() => {
              if (selectedAccountId === null) return 'Select Account';
              const account = accounts.find(a => a.id === selectedAccountId);
              return account ? account.name : 'Select Account';
            }}
            startAdornment={
              loadingAccounts ? (
                <CircularProgress size={20} color="inherit" sx={{ ml: 1, mr: 1 }} />
              ) : null
            }
          >
            {accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </Select>
          {errors.accountId && <FormHelperText>{errors.accountId}</FormHelperText>}
        </FormControl>
        
        {/* Solution Selection */}
        <FormControl 
          fullWidth 
          margin="normal" 
          error={!!errors.solutionId}
          disabled={!selectedAccountId || loadingSolutions || saving}
        >
          <InputLabel id="solution-select-label">Solution</InputLabel>
          <Select
            labelId="solution-select-label"
            id="solution-select"
            value={selectedSolutionId || ''}
            onChange={handleSolutionChange}
            label="Solution"
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return 'Select Solution';
              const solution = solutions.find(s => s.id === selected);
              return solution ? solution.name : 'Select Solution';
            }}
            startAdornment={
              loadingSolutions ? (
                <CircularProgress size={20} color="inherit" sx={{ ml: 1, mr: 1 }} />
              ) : null
            }
          >
            {solutions.map((solution) => (
              <MenuItem key={solution.id} value={solution.id}>
                {solution.name}
              </MenuItem>
            ))}
            {solutions.length === 0 && !loadingSolutions && (
              <MenuItem disabled>
                No solutions available
              </MenuItem>
            )}
          </Select>
          {errors.solutionId && <FormHelperText>{errors.solutionId}</FormHelperText>}
        </FormControl>
        
        {/* Subscription Selection */}
        <FormControl 
          fullWidth 
          margin="normal" 
          error={!!errors.subscriptionId}
          disabled={!selectedAccountId || loadingSubscriptions || saving}
        >
          <InputLabel id="subscription-select-label">Subscription</InputLabel>
          <Select
            labelId="subscription-select-label"
            id="subscription-select"
            value={selectedSubscriptionId || ''}
            onChange={handleSubscriptionChange}
            label="Subscription"
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return 'Select Subscription';
              const subscription = subscriptions.find(s => s.id === selected);
              return subscription ? subscription.name : 'Select Subscription';
            }}
            startAdornment={
              loadingSubscriptions ? (
                <CircularProgress size={20} color="inherit" sx={{ ml: 1, mr: 1 }} />
              ) : null
            }
          >
            {subscriptions?.map((subscription) => (
                <MenuItem key={subscription.id} value={subscription.id}>
                  {subscription.name}
                </MenuItem>
              ))}
            {subscriptions?.length === 0 && !loadingSubscriptions && (
              <MenuItem disabled>
                No Subscriptions available
              </MenuItem>
            )}
          </Select>
          {errors.subscriptionId && <FormHelperText>{errors.subscriptionId}</FormHelperText>}
        </FormControl>
        
        {/* Loading indicator for feedback config */}
        {loadingSchedules && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} color="primary" />
          </Box>
        )}
        
        {/* Summary Schedules - Only shown if allowSummaryNotifications is true */}
        {showSchedules && (
          <FormControl 
            fullWidth 
            margin="normal"
            disabled={loadingSchedules || saving}
          >
            <InputLabel id="schedules-select-label">Notification Schedules</InputLabel>
            <Select
              labelId="schedules-select-label"
              id="schedules-select"
              multiple
              value={selectedScheduleIds}
              onChange={handleSchedulesChange}
              input={<OutlinedInput label="Notification Schedules" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    // Try to find the schedule in the loaded schedules array
                    const schedule = schedules.find(s => s.summaryScheduleId === value || s.id === value);
                    
                    // Get the name from the schedule if it exists
                    let scheduleName = schedule?.name;
                    
                    // Log the API response for debugging
                    log.log(`API schedule found for value ${value}:`, schedule);
                    if (schedule) {
                      log.log(`API schedule summaryScheduleId: ${schedule.summaryScheduleId}, id: ${schedule.id}`);
                      // Check if summaryScheduleId and id match
                      if (schedule.summaryScheduleId === schedule.id) {
                        log.log(`Match found! summaryScheduleId and id both equal ${schedule.id}`);
                      }
                    }
                    
                    // If name is still missing and we're in edit mode, try to find it in the subscription data
                    if (!scheduleName && editMode && subscription && Array.isArray(subscription.summarySchedules)) {
                      // Try to find by summaryScheduleId first, then by id if that fails
                      const subscriptionSchedule = subscription.summarySchedules.find(
                        s => s.summaryScheduleId === value || s.id === value
                      );
                      
                      // Log the subscription schedule for debugging
                      log.log(`Subscription schedule found for value ${value}:`, subscriptionSchedule);
                      if (subscriptionSchedule) {
                        log.log(`Subscription schedule summaryScheduleId: ${subscriptionSchedule.summaryScheduleId}, id: ${subscriptionSchedule.id}`);
                        // Check if summaryScheduleId and id match
                        if (subscriptionSchedule.summaryScheduleId === subscriptionSchedule.id) {
                          log.log(`Match found! summaryScheduleId and id both equal ${subscriptionSchedule.id}`);
                        }
                        
                        if (subscriptionSchedule.name) {
                          scheduleName = subscriptionSchedule.name;
                        }
                      }
                    }
                    
                    // Log for debugging
                    log.log(`Rendering schedule chip for ID ${value}, found name: ${scheduleName || 'not found'}`);
                    
                    // Use a descriptive fallback if name is still missing
                    const displayName = scheduleName || `Schedule ${value}`;
                    
                    return (
                      <Chip 
                        key={value} 
                        label={displayName}
                        size="small"
                        sx={{ 
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          fontWeight: 500
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              startAdornment={
                loadingSchedules ? (
                  <CircularProgress size={20} color="inherit" sx={{ ml: 1, mr: 1 }} />
                ) : null
              }
            >
              {schedules?.map((schedule) => {
                // Log the schedule for debugging
                log.log(`Rendering menu item for schedule:`, schedule);
                
                // Determine which ID to use as the value
                // If summaryScheduleId exists, use that, otherwise use id
                const valueId = schedule.summaryScheduleId ?? schedule.id;
                
                // Check if summaryScheduleId and id match and log it
                if (schedule.summaryScheduleId !== undefined && schedule.id !== undefined) {
                  if (schedule.summaryScheduleId === schedule.id) {
                    log.log(`Menu item: Match found! summaryScheduleId and id both equal ${schedule.id}`);
                  } else {
                    log.log(`Menu item: No match. summaryScheduleId=${schedule.summaryScheduleId}, id=${schedule.id}`);
                  }
                }
                
                return (
                  <MenuItem key={valueId} value={valueId}>
                    <Box>
                      <Typography variant="body1">{schedule.name}</Typography>
                      {schedule.summaryScheduleId !== undefined && schedule.id !== undefined && (
                        <Typography variant="caption" color="text.secondary">
                          ID: {schedule.id}, ScheduleID: {schedule.summaryScheduleId}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
              {schedules.length === 0 && !loadingSchedules && (
                <MenuItem disabled>
                  No schedules available
                </MenuItem>
              )}
            </Select>
            <FormHelperText>Optional: Select notification schedules for this subscription</FormHelperText>
          </FormControl>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{ 
            borderRadius: 1,
            minWidth: 100,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={(e) => handleSubmit(e)}
          disabled={saving}
          variant="contained" 
          color="primary"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ 
            borderRadius: 1,
            minWidth: 100,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 3px 6px rgba(0,0,0,0.15)'
          }}
        >
          {saving ? 'Saving...' : editMode ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSubscriptionDialog;